import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useIsScreeningAvailable } from "@js/apps/freelancer/hooks/use-is-screening-available";
import {
  StartIdentityVerificationModalContent,
  useUserNeedsIdentityVerification,
} from "@js/apps/identity-verification";
import { ModalInstance } from "@js/components/modal";
import { useNavigate } from "@js/hooks";

import {
  FreelancerScreenedModalContent,
  openFreelancerScreenedModal,
} from "./freelancer-screened-modal-content";
import { StartScreeningModalContent } from "./start-screening-modal-content";
import type { OpenBidCreateSuccessModalArg } from "./types";

import styles from "./styles.module.scss";

export const closeBidCreateSuccessModal = ModalInstance.close;

export const openBidCreateSuccessModal = ({
  roleId,
  onKeyDown,
  isScaleAiBid,
}: OpenBidCreateSuccessModalArg) =>
  ModalInstance.open({
    children: (
      <BidSentSuccessfullyModalContent
        closeModal={ModalInstance.close}
        roleId={roleId}
        isScaleAiBid={isScaleAiBid}
      />
    ),
    closeButton: false,
    className: styles.modal,
    keepOnBackdropClick: true,
    padding: false,
    ...(onKeyDown ? { onKeyDown } : {}),
  });

type BidSentSuccessfullyModalContentProps = {
  closeModal: () => void;
} & OpenBidCreateSuccessModalArg;

const BidSentSuccessfullyModalContent = ({
  closeModal,
  roleId,
  isScaleAiBid,
}: BidSentSuccessfullyModalContentProps) => {
  const mounted = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { userNeedsIdentityVerification } = useUserNeedsIdentityVerification();
  const { isScreeningAvailable, isLoading } = useIsScreeningAvailable();

  const showStartScreeningModal = isScreeningAvailable && !isScaleAiBid;

  useEffect(() => {
    return () => {
      if (mounted.current) {
        closeModal();
      } else {
        mounted.current = true;
      }
    };
  }, [location, closeModal]);

  if (isLoading) {
    return <></>;
  }

  if (userNeedsIdentityVerification) {
    return (
      <StartIdentityVerificationModalContent
        closeModal={closeModal}
        onVerificationFail={() => {
          navigate("/jobs/");
        }}
        onVerificationSuccess={() => {
          openFreelancerScreenedModal({ roleId });
        }}
      />
    );
  }

  if (showStartScreeningModal) {
    return <StartScreeningModalContent closeModal={closeModal} />;
  }

  return (
    <FreelancerScreenedModalContent roleId={roleId} closeModal={closeModal} />
  );
};
